import { RippleButton } from "./RippleButton";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "../../../Components/InputFields/Select";
import Simple from "../../../Components/InputFields/Simple";
import SVG from "../../../Components/SVG";
import { API_BASE_URL } from "../../../config/api";

const cardClasses = `
    w-full max-w-md backdrop-blur-[14px] mx-auto rounded-2xl min-w-0 max-h-full p-3 sm:p-5 flex flex-col md:flex-row transition-all duration-300
    bg-gradient-to-br from-white/10 to-white/5 border border-white/10
  `;
const buttonClasses = `
     bg-cyan-600 hocus:opacity-85 text-black
     w-full py-1 rounded-xl font-medium text-base transition mt-3 disabled:bg-white/10 disabled:text-white/50 disabled:cursor-not-allowed
  `;
const GATEWAYS_URL = `${API_BASE_URL}payment-payout/gateways/`;
const PAY_URL = `${API_BASE_URL}payment-payout/pay/`;
const PAY_GATEWAY_URL = `${API_BASE_URL}payment-payout/nowpayments/checkout/`;
const PAY_GATEWAY_URL_IRR = `${API_BASE_URL}payment-payout/directpay/checkout/`;
const REFRESH_URL = `${API_BASE_URL}auth/trader/jwt/refresh/`;

const resolvePaymentErrorMessage = (errorData) =>
  errorData?.detail ||
  errorData?.message ||
  errorData?.error ||
  "ثبت پرداخت با خطا مواجه شد. لطفا دوباره تلاش کنید.";

function PaymentErrorAlert({ message }) {
  if (!message) return null;

  return (
    <div
      role="alert"
      className="mt-4 flex items-start gap-2 rounded-xl border border-amber-500/45 bg-amber-500/15 px-3 py-2.5 text-right text-amber-200 shadow-sm shadow-amber-950/5"
    >
      <SVG
        name="danger"
        classname="mt-0.5 size-5 shrink-0 fill-amber-600 stroke-white dark:fill-amber-400 dark:stroke-amber-950"
      />
      <p className="text-xs leading-6 sm:text-sm">{message}</p>
    </div>
  );
}

export default function PaymentMethod({ plan, payablePrice, payablePriceIrr }) {
  const navigate = useNavigate();

  const trainingOptions = [
    {
      id: "crypto-gateway",
      title: "درگاه پرداخت ارز دیجیتال",
      description: "فعال‌سازی آنی حساب",
      svg: "nowPayment",
    },
    {
      id: "rial-gateway",
      title: "درگاه پرداخت ریالی",
      description: "فعال‌سازی آنی حساب",
      svg: "shaparak",
    },
    {
      id: "crypto-address",
      title: "کیف پول ارز دیجیتال",
      description: "فعال‌سازی حساب حداکثر ۸ ساعت",
      svg: "cryptoWallet",
    },
    // { id: "rial-gateway", title: "درگاه پرداخت ریالی", description: "فعال‌سازی آنی حساب" },
    // { id: "rial-address", title: "حساب به حساب ریالی", description: "فعال‌سازی حساب حداکثر ۸ ساعت", svg: "rialWallet" },
  ];

  const [selectedOption, setSelectedOption] = useState(trainingOptions[0].id);
  const [gatewayMap, setGatewayMap] = useState({});
  const [walletOptions, setWalletOptions] = useState({});
  const [transactionId, setTransactionId] = useState("");
  const [coin, setCoin] = useState("USDT");
  const [network, setNetwork] = useState("");
  const [copied, setCopied] = useState(false);
  const copiedTimeoutRef = useRef(null);
  const [validationError, setValidationError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittingGateway, setIsSubmittingGateway] = useState(false);
  const selectedGateway = network ? (gatewayMap[network] ?? null) : null;
  const selectedGatewayCode = selectedGateway?.code ?? "";
  const selectedWallet = network ? (walletOptions[network] ?? "") : "";
  const networkOptions = Object.keys(walletOptions);
  const planName = plan?.planName ?? "-";
  const displayPayablePrice = payablePrice === "" ? "-" : payablePrice;
  const displayPayablePriceIrr =
    payablePriceIrr === "" ? "-" : `${payablePriceIrr.toLocaleString()} ریال`;

  const logout = useCallback(() => {
    localStorage.removeItem("access");
    localStorage.removeItem("authCredentials");
    localStorage.removeItem("refresh");
    localStorage.removeItem("role");
    localStorage.removeItem("email");
    navigate("/signin");
  }, [navigate]);

  const refreshAccessToken = useCallback(async () => {
    const refreshResponse = await fetch(REFRESH_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        refresh: localStorage.getItem("refresh"),
      }),
    });

    if (!refreshResponse.ok) {
      logout();
      return null;
    }

    const refreshData = await refreshResponse.json();
    localStorage.setItem("access", refreshData.access);
    return refreshData.access;
  }, [logout]);

  const fetchWithAuthRetry = useCallback(
    async (url, options = {}) => {
      const getRequestOptions = () => ({
        ...options,
        headers: {
          "Content-Type": "application/json",
          ...(options.headers ?? {}),
          Authorization: `Bearer ${localStorage.getItem("access")}`,
        },
      });

      let response = await fetch(url, getRequestOptions());

      if (response.status !== 401) {
        return response;
      }

      const nextAccessToken = await refreshAccessToken();
      if (!nextAccessToken) {
        return null;
      }

      return fetch(url, getRequestOptions());
    },
    [refreshAccessToken],
  );

  useEffect(() => {
    const fetchGateways = async () => {
      try {
        const response = await fetchWithAuthRetry(GATEWAYS_URL, {
          method: "GET",
        });
        if (!response) {
          return;
        }

        if (!response.ok) {
          throw new Error(`https error! Status: ${response.status}`);
        }

        const data = await response.json();
        const gateways = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : [];
        const nextGatewayMap = gateways.reduce((options, gateway) => {
          if (gateway?.name) {
            options[gateway.name] = {
              code: gateway.code ?? "",
              address: gateway.address ?? "",
            };
          }
          return options;
        }, {});
        const nextWalletOptions = gateways.reduce((options, gateway) => {
          if (gateway?.name && gateway?.address) {
            options[gateway.name] = gateway.address;
          }
          return options;
        }, {});

        setGatewayMap(nextGatewayMap);
        setWalletOptions(nextWalletOptions);
        setNetwork((currentNetwork) =>
          currentNetwork && nextGatewayMap[currentNetwork]
            ? currentNetwork
            : (Object.keys(nextGatewayMap)[0] ?? ""),
        );
      } catch (error) {
        console.error("Error fetching payment gateways:", error);
      }
    };

    fetchGateways();
  }, [fetchWithAuthRetry]);

  useEffect(() => {
    setCopied(false);

    if (copiedTimeoutRef.current) {
      clearTimeout(copiedTimeoutRef.current);
      copiedTimeoutRef.current = null;
    }

    return () => {
      if (copiedTimeoutRef.current) {
        clearTimeout(copiedTimeoutRef.current);
      }
    };
  }, [selectedWallet]);

  const handleCopyWallet = async () => {
    if (!selectedWallet || !navigator?.clipboard?.writeText) return;

    try {
      await navigator.clipboard.writeText(String(selectedWallet));
      setCopied(true);

      if (copiedTimeoutRef.current) {
        clearTimeout(copiedTimeoutRef.current);
      }

      copiedTimeoutRef.current = setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy wallet address:", err);
    }
  };

  const handleSubmitPayment = async () => {
    const trimmedTransactionId = transactionId.trim();

    if (!selectedGatewayCode || !trimmedTransactionId) {
      setValidationError(
        "انتخاب شبکه و وارد کردن کد پیگیری پرداخت الزامی است.",
      );
      setSubmitError("");
      return;
    }

    setValidationError("");
    setSubmitError("");
    setIsSubmitting(true);

    try {
      const response = await fetchWithAuthRetry(PAY_URL, {
        method: "POST",
        body: JSON.stringify({
          payment_gateway_code: selectedGatewayCode,
          transaction_id: trimmedTransactionId,
        }),
      });

      if (!response) {
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        setSubmitError(resolvePaymentErrorMessage(errorData));
        return;
      }

      navigate("/purchase/finalized/manual");
    } catch (error) {
      console.error("Error submitting payment:", error);
      setSubmitError("ثبت پرداخت با خطا مواجه شد. لطفا دوباره تلاش کنید.");
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleSubmitPaymentGateway = async () => {
    setValidationError("");
    setSubmitError("");
    setIsSubmittingGateway(true);

    try {
      const response = await fetchWithAuthRetry(
        selectedOption === trainingOptions[1].id
          ? PAY_GATEWAY_URL_IRR
          : PAY_GATEWAY_URL,
        {
          method: "POST",
        },
      );

      if (!response) {
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        setSubmitError(resolvePaymentErrorMessage(errorData));
        return;
      }

      const data = await response.json().catch(() => null);
      const checkoutUrl =
        data?.checkout_url || data?.data?.checkout_url || data?.url;

      if (!checkoutUrl) {
        setSubmitError("لینک درگاه پرداخت دریافت نشد. لطفا دوباره تلاش کنید.");
        return;
      }

      window.location.href = checkoutUrl;
    } catch (error) {
      console.error("Error submitting payment:", error);
      setSubmitError("ثبت پرداخت با خطا مواجه شد. لطفا دوباره تلاش کنید.");
    } finally {
      setIsSubmittingGateway(false);
    }
  };
  return (
    <main className="relative w-full max-w-5xl max-h-full flex flex-col items-center justify-start mx-auto">
      <div className="w-full flex justify-center max-h-full">
        <div className={cardClasses.trim()}>
          <div className="flex-1 flex flex-col overflow-y-auto">
            <div className="pb-3">
              <p className="text-sm font-thin text-center w-full text-[#fafafa]">
                روش پرداخت
              </p>
            </div>

            <div className="hidden card-divider w-full h-px my-3 bg-white/20" />

            <div className="w-full flex flex-col justify-center items-center gap-1 mb-3">
              <div className="flex items-center justify-between gap-2 w-full">
                <span className="text-sm text-[#fafafa]/70">
                  چالش انتخاب شده
                </span>
                <span className="text-lg font-extralight text-[#fafafa]">
                  {planName}
                </span>
              </div>

              <div className="flex items-center justify-between gap-2 w-full">
                <span className="text-sm text-[#fafafa]/70">
                  مبلغ قابل پرداخت
                </span>
                <span className="text-lg font-extralight text-[#fafafa]">
                  {selectedOption === trainingOptions[1].id
                    ? displayPayablePriceIrr
                    : displayPayablePrice === "-"
                      ? displayPayablePrice
                      : `$${displayPayablePrice}`}
                </span>
              </div>
              <div className="card-divider w-full h-px mt-2 bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.09)_20%,rgba(255,255,255,0.22)_50%,rgba(255,255,255,0.09)_80%,transparent)]" />
            </div>

            {/* <p className="font-light text-xs text-zi-text-light dark:text-zi-text-dark mb-6">
                  توجه: در زمان قطعی اینترنت بین‌الملل، درگاه پرداخت ارز دیجیتال در دسترس نیست.
                </p> */}
            <div className="flex-1">
              <p className="font-light text-xs text-[#fafafa] mb-3">
                روش پرداخت را انتخاب کنید:
              </p>

              <div className="flex flex-col gap-2">
                {trainingOptions.map((option) => {
                  const isSelected = option.id === selectedOption;
                  return (
                    <label
                      key={option.id}
                      className={`relative w-full flex items-center justify-between gap-1 rounded-xl border px-2 py-1.5 cursor-pointer transition-colors ${
                        isSelected
                          ? "border-zi-border-dark/50 bg-zi-inputbg-dark"
                          : "border-transparent bg-zi-inputbg-dark/50"
                      }`}
                    >
                      <div className="flex items-center gap-2 text-right">
                        <span
                          className={`size-4 rounded-full border ${
                            isSelected
                              ? "border-zi-border-dark bg-cyan-600"
                              : "border-zi-border-dark/50"
                          }`}
                        />
                        <div className="flex flex-col gap-0.5">
                          <p className="text-xs sm:text-sm font-medium text-[#fafafa] whitespace-nowrap">
                            {option.title}
                          </p>
                          <p className="text-[10px] sm:text-xs text-[#fafafa]/60 whitespace-nowrap">
                            {option.description}
                          </p>
                        </div>
                      </div>
                      <input
                        type="radio"
                        name="demoTrainingOption"
                        value={option.id}
                        checked={isSelected}
                        onChange={() => {
                          setSelectedOption(option.id);
                          setValidationError("");
                          setSubmitError("");
                        }}
                        className="sr-only"
                      />
                      <SVG name={option.svg} classname="" />
                    </label>
                  );
                })}
              </div>

              {selectedOption === trainingOptions[2].id && (
                <>
                  <p className="font-light text-xs text-[#fafafa] mt-5 w-full">
                    ابتدا رمز ارز و شبکه مورد نظر را انتخاب کنید تا آدرس کیف پول
                    ایجاد گردد.
                  </p>

                  <div className="flex items-center gap-2.5 mt-5">
                    <div className="relative w-full">
                      <Select
                        className="h-[42px] overflow-hidden rounded-lg border-none bg-transparent dark:bg-zi-inputbg-dark [&_select]:!h-full [&_select]:!rounded-none [&_select]:!border-0 [&_select]:!bg-transparent [&_select]:!pr-3 [&_select]:!pl-10 [&_select]:!py-0 [&_select]:!text-sm [&_select]:hocus:!ring-0"
                        selectClassName="[direction:ltr] text-left !border-0 !outline-none !ring-0"
                        showFocusRing={false}
                        options={["USDT"]}
                        value={coin}
                        onChange={(e) => {
                          setCoin(e.target.value);
                        }}
                      />
                      <span className="pointer-events-none absolute right-2 top-0 z-10 -translate-y-1/2 rounded-lg px-2.5 py-1 text-xs leading-none text-zi-blue-light dark:bg-zi-inputbg-dark dark:text-zi-text-dark">
                        رمز ارز
                      </span>
                    </div>
                    <div className="relative w-full">
                      <Select
                        className="h-[42px] overflow-hidden rounded-lg border-none bg-transparent dark:bg-zi-inputbg-dark [&_select]:!h-full [&_select]:!rounded-none [&_select]:!border-0 [&_select]:!bg-transparent [&_select]:!pr-3 [&_select]:!pl-10 [&_select]:!py-0 [&_select]:!text-sm [&_select]:hocus:!ring-0"
                        selectClassName="[direction:ltr] text-left !border-0 !outline-none !ring-0"
                        showFocusRing={false}
                        options={networkOptions}
                        value={network}
                        onChange={(e) => {
                          setNetwork(e.target.value);
                          setValidationError("");
                          setSubmitError("");
                        }}
                      />
                      <span
                        className={`pointer-events-none absolute right-2 z-10 origin-right rounded-lg px-2.5 py-1 leading-none transition-all duration-200 ease-in-out dark:bg-zi-inputbg-dark ${
                          network
                            ? "top-0 -translate-y-1/2 text-xs text-zi-blue-light dark:text-zi-text-dark"
                            : "top-1/2 -translate-y-1/2 text-sm text-zi-text-light/50 dark:text-zi-text-dark/50"
                        }`}
                      >
                        شبکه
                      </span>
                    </div>
                  </div>

                  <div className="w-full mt-5">
                    <div className="relative w-full [&_input]:!pr-9">
                      <Simple
                        placeholder="آدرس کیف پول"
                        type="text"
                        id="wallet-address"
                        formValue={selectedWallet}
                        readOnly
                      />
                      <button
                        type="button"
                        onClick={handleCopyWallet}
                        className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center justify-center p-1"
                        aria-label="کپی آدرس کیف پول"
                      >
                        {selectedWallet && (
                          <SVG
                            name={copied ? "tick" : "copy"}
                            classname={
                              copied
                                ? "size-4 cursor-pointer fill-zi-text-light stroke-zi-text-dark dark:fill-zi-text-dark dark:stroke-zi-text-light"
                                : "size-4 cursor-pointer stroke-zi-text-light dark:stroke-zi-text-dark"
                            }
                          />
                        )}
                      </button>
                    </div>
                  </div>

                  <p className="font-light text-xs text-[#fafafa] mt-5">
                    پس از تکمیل پرداخت به آدرس کیف پول، کد پیگیری پرداخت را در
                    قسمت زیر وارد کنید.
                  </p>

                  <div className="w-full mt-5">
                    <Simple
                      placeholder="کد پیگیری پرداخت"
                      type="text"
                      id="payment-transaction-id"
                      formValue={transactionId}
                      formOnChange={(e) => {
                        setTransactionId(e.target.value);
                        setValidationError("");
                        setSubmitError("");
                      }}
                    />
                  </div>
                  <PaymentErrorAlert message={validationError || submitError} />

                  <p className="font-light text-xs text-[#fafafa] mt-5">
                    در نهایت، برروی دکمه تأیید پرداخت بزنید. حداکثر ظرف ۸ ساعت
                    حساب موردنظر برای شما فعال می‌گردد.
                  </p>
                  <RippleButton
                    className={buttonClasses.trim()}
                    onClick={handleSubmitPayment}
                    disabled={isSubmitting}
                    ruleAgree={false}
                  >
                    {isSubmitting ? "در حال ثبت..." : "تأیید پرداخت"}
                  </RippleButton>
                </>
              )}
              {(selectedOption === trainingOptions[0].id ||
                selectedOption === trainingOptions[1].id) && (
                <RippleButton
                  className={buttonClasses.trim()}
                  onClick={handleSubmitPaymentGateway}
                  disabled={isSubmittingGateway}
                  ruleAgree={false}
                >
                  {isSubmittingGateway
                    ? "در حال ثبت..."
                    : "انتقال به درگاه پرداخت"}
                </RippleButton>
              )}
              {selectedOption === trainingOptions[0].id && (
                <PaymentErrorAlert message={submitError} />
              )}
            </div>
          </div>

          <div className="hidden card-divider w-full h-px my-3 bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.09)_20%,rgba(255,255,255,0.22)_50%,rgba(255,255,255,0.09)_80%,transparent)] md:mb-0 md:mx-5 md:w-px md:h-auto md:self-stretch md:shrink-0 md:bg-[linear-gradient(180deg,transparent,rgba(255,255,255,0.09)_20%,rgba(255,255,255,0.22)_50%,rgba(255,255,255,0.09)_80%,transparent)]" />
        </div>
      </div>
    </main>
  );
}
